import type { PartnerId } from '@/constants/partners'

import { cn } from '@/lib/utils'

interface PartnerWordmarkProps {
    id: PartnerId
    className?: string
}

/** Invented partner and press wordmarks, drawn as type so they stay crisp. */
export const PartnerWordmark = ({ id, className }: PartnerWordmarkProps) => {
    const base = cn('inline-flex items-center gap-1.5 whitespace-nowrap text-cream/80', className)

    switch (id) {
        case 'northfield':
            return (
                <span className={base}>
                    <svg viewBox='0 0 16 16' aria-hidden='true' className='size-3.5'>
                        <path d='M8 1.5 13.5 14.5 8 11 2.5 14.5Z' fill='currentColor' />
                    </svg>

                    <span className='font-serif text-[1.35em] italic leading-none tracking-[-0.01em]'>
                        Northfield
                    </span>
                </span>
            )
        case 'halcyon':
            return (
                <span className={base}>
                    <svg viewBox='0 0 16 16' aria-hidden='true' className='size-3.5'>
                        <circle cx='8' cy='8' r='6.2' fill='none' stroke='currentColor' strokeWidth='1.4' />
                        <path d='M1.8 8a6.2 6.2 0 0 0 12.4 0Z' fill='currentColor' />
                    </svg>

                    <span className='text-[0.92em] font-light uppercase leading-none tracking-[0.32em]'>
                        Halcyon
                    </span>
                </span>
            )
        case 'meridian':
            return (
                <span className={base}>
                    <span className='text-[1.08em] font-semibold lowercase leading-none tracking-[-0.05em]'>
                        meridian
                    </span>

                    <span aria-hidden='true' className='-mt-2 size-1.5 rounded-full bg-current' />

                    <span className='text-[0.7em] font-medium uppercase leading-none tracking-[0.2em] text-cream/50'>
                        Trust
                    </span>
                </span>
            )
        case 'ashgrove':
            return (
                <span className={cn(base, 'gap-1 font-serif text-[1.3em] leading-none tracking-[-0.01em]')}>
                    <span>
                        Ashgrove
                    </span>

                    <span className='italic text-cream/60'>
                        &amp;
                    </span>

                    <span>
                        Co
                    </span>
                </span>
            )
        case 'lumen':
            return (
                <span className={base}>
                    <svg viewBox='0 0 16 16' aria-hidden='true' className='size-3.5'>
                        <circle cx='8' cy='8' r='2.6' fill='currentColor' />
                        <path d='M8 1v2.4M8 12.6V15M1 8h2.4M12.6 8H15M3 3l1.7 1.7M11.3 11.3 13 13M3 13l1.7-1.7M11.3 4.7 13 3' stroke='currentColor' strokeWidth='1.3' strokeLinecap='round' />
                    </svg>

                    <span className='text-[0.95em] font-bold uppercase leading-none tracking-[0.08em]'>
                        Lumen
                    </span>

                    <span className='text-[0.95em] font-light uppercase leading-none tracking-[0.08em] text-cream/55'>
                        Capital
                    </span>
                </span>
            )
        case 'stillwater':
            return (
                <span className={base}>
                    <svg viewBox='0 0 18 12' aria-hidden='true' className='h-3 w-[18px]'>
                        <path d='M1 4c2.7-2.6 5.3 2.6 8 0s5.3 2.6 8 0M1 9c2.7-2.6 5.3 2.6 8 0s5.3 2.6 8 0' fill='none' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' />
                    </svg>

                    <span className='text-[1.02em] font-medium leading-none tracking-[-0.035em]'>
                        Stillwater
                    </span>
                </span>
            )
        default:
            return null
    }
}
